import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Paper,
  Typography,
  Chip,
  Divider,
  Grid,
  Tooltip,
  Button,
  CircularProgress,
  Box,
  Collapse,
} from "@material-ui/core";
import {
  FlightTakeoff as FlightIcon,
  People as PeopleIcon,
  AttachMoney as BudgetIcon,
  Event as DateIcon,
  Hotel as HotelIcon,
  Star as RequirementsIcon,
  Language as LanguageIcon,
  CheckCircle as ReadyIcon,
  Warning as PendingIcon,
  Error as MissingIcon,
  Assignment as SummaryIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  FileCopy as CopyIcon,
} from "@material-ui/icons";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  profileContainer: {
    padding: theme.spacing(1.5),
    marginTop: theme.spacing(1),
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(1),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
  sectionIcon: {
    fontSize: 16,
  },
  infoRow: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(0.5),
    fontSize: 12,
  },
  infoLabel: {
    color: theme.palette.text.secondary,
    fontSize: 11,
    minWidth: 80,
  },
  infoValue: {
    fontSize: 12,
    fontWeight: 500,
  },
  chip: {
    height: 20,
    fontSize: 10,
    margin: theme.spacing(0.25),
  },
  chipContainer: {
    display: "flex",
    flexWrap: "wrap",
    marginTop: theme.spacing(0.5),
  },
  confidenceBadge: {
    fontSize: 9,
    marginLeft: theme.spacing(0.5),
    padding: "1px 4px",
    borderRadius: 4,
    backgroundColor: theme.palette.grey[200],
  },
  divider: {
    margin: theme.spacing(1, 0),
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(2),
    color: theme.palette.text.secondary,
    fontSize: 12,
  },
  // Ready-to-Book styles
  readyToBookSection: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1.5),
    borderRadius: 8,
    backgroundColor: theme.palette.type === "dark" ? "#1a2332" : "#f8fafc",
  },
  readyToBookHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    cursor: "pointer",
    "&:hover": {
      opacity: 0.8,
    },
  },
  statusReady: {
    color: "#4caf50",
    display: "flex",
    alignItems: "center",
    gap: 4,
    fontSize: 12,
    fontWeight: 600,
  },
  statusPending: {
    color: "#ff9800",
    display: "flex",
    alignItems: "center",
    gap: 4,
    fontSize: 12,
    fontWeight: 600,
  },
  statusMissing: {
    color: "#f44336",
    display: "flex",
    alignItems: "center",
    gap: 4,
    fontSize: 12,
    fontWeight: 600,
  },
  missingItem: {
    fontSize: 11,
    color: theme.palette.text.secondary,
    padding: "2px 0",
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  summaryBox: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.type === "dark" ? "#0f172a" : "#ffffff",
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
    fontSize: 12,
    whiteSpace: "pre-wrap",
    fontFamily: "inherit",
    position: "relative",
  },
  copyButton: {
    position: "absolute",
    top: 4,
    right: 4,
    padding: 4,
    minWidth: "auto",
  },
  generateButton: {
    marginTop: theme.spacing(1),
    fontSize: 11,
    textTransform: "none",
  },
}));

const getConfidenceColor = (confidence) => {
  if (confidence >= 0.8) return "#4caf50";
  if (confidence >= 0.5) return "#ff9800";
  return "#f44336";
};

const ContactProfile = ({ profile, ticketId, contactId }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);

  // Calculate Ready-to-Book status
  const getReadyToBookStatus = () => {
    if (!profile || Object.keys(profile).length === 0) {
      return { status: "missing", missingFields: [t("contactProfile.profileNotBuilt")] };
    }

    const missingFields = [];
    const { personal, travel, family } = profile;

    // Required fields for booking
    if (!personal?.fullName) missingFields.push(t("contactProfile.fields.customerName"));
    if (!travel?.destinations?.length) missingFields.push(t("contactProfile.fields.destination"));
    if (!travel?.preferredMonths?.length && !travel?.travelDates) missingFields.push(t("contactProfile.fields.travelDates"));
    if (!family?.adultsCount) missingFields.push(t("contactProfile.fields.adultsCount"));
    if (family?.childrenCount > 0 && !family?.childrenAges?.length) missingFields.push(t("contactProfile.fields.childrenAges"));

    if (missingFields.length === 0) {
      return { status: "ready", missingFields: [] };
    } else if (missingFields.length <= 2) {
      return { status: "pending", missingFields };
    } else {
      return { status: "missing", missingFields };
    }
  };

  const handleGenerateSummary = async () => {
    if (!ticketId) {
      toast.warning(t("contactProfile.toasts.ticketRequired"));
      return;
    }
    
    setLoading(true);
    try {
      const { data } = await api.post("/offers/ready-to-book", {
        ticketId,
        contactProfile: profile,
        conversationContext: [], // Context will be fetched by backend if needed
      });
      setSummary(data);
    } catch (err) {
      console.error("Failed to generate summary:", err);
      toast.error(t("contactProfile.toasts.generateFailed"));
    } finally {
      setLoading(false);
    }
  };

  const handleCopySummary = () => {
    if (summary?.summaryText) {
      navigator.clipboard.writeText(summary.summaryText);
      toast.success(t("contactProfile.toasts.copied"));
    }
  };

  const readyStatus = getReadyToBookStatus();

  const hasProfile = profile && Object.keys(profile).length > 0;
  
  if (!hasProfile) {
    return (
      <Paper square variant="outlined" className={classes.profileContainer}>
        <Typography variant="subtitle1" style={{ marginBottom: 8 }}>
          🧠 {t("contactProfile.title")}
        </Typography>
        <Typography className={classes.emptyState}>
          {t("contactProfile.emptyState")}
        </Typography>
        
        {/* Ready-to-Book Section - always visible */}
        <Paper className={classes.readyToBookSection} elevation={0}>
          <div 
            className={classes.readyToBookHeader}
            onClick={() => setExpanded(!expanded)}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <SummaryIcon style={{ fontSize: 18, color: "#25d366" }} />
              <Typography style={{ fontSize: 13, fontWeight: 600 }}>
                {t("contactProfile.readyToBook.title")}
              </Typography>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span className={classes.statusMissing}>
                <MissingIcon style={{ fontSize: 14 }} />
                {t("contactProfile.readyToBook.noProfile")}
              </span>
              {expanded ? <CollapseIcon /> : <ExpandIcon />}
            </div>
          </div>

          <Collapse in={expanded}>
            <Box mt={1}>
              <Typography style={{ fontSize: 11, color: "#666", marginBottom: 8 }}>
                {t("contactProfile.readyToBook.noProfileDesc")}
              </Typography>
              
              {ticketId && (
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  fullWidth
                  className={classes.generateButton}
                  onClick={handleGenerateSummary}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={14} /> : <SummaryIcon />}
                >
                  {loading ? t("contactProfile.readyToBook.generating") : t("contactProfile.readyToBook.tryGenerate")}
                </Button>
              )}

              {summary && (
                <Box className={classes.summaryBox}>
                  <Button
                    size="small"
                    className={classes.copyButton}
                    onClick={handleCopySummary}
                    title={t("contactProfile.readyToBook.copyToClipboard")}
                  >
                    <CopyIcon style={{ fontSize: 14 }} />
                  </Button>
                  <Typography style={{ fontSize: 11, fontWeight: 600, marginBottom: 8 }}>
                    {summary.status === "ready" ? `✅ ${t("contactProfile.readyToBook.statusReadyFull")}` : 
                     summary.status === "pending" ? `⏳ ${t("contactProfile.readyToBook.statusAlmostFull")}` : `❌ ${t("contactProfile.readyToBook.statusMoreInfo")}`}
                  </Typography>
                  {summary.summaryText}
                </Box>
              )}
            </Box>
          </Collapse>
        </Paper>
      </Paper>
    );
  }

  const { personal, travel, family, interests, confidence = {} } = profile;

  const getConfidence = (key) => {
    return confidence[key] || confidence[`personal.${key}`] || confidence[`travel.${key}`] || confidence[`family.${key}`] || null;
  };

  const ConfidenceBadge = ({ fieldKey }) => {
    const conf = getConfidence(fieldKey);
    if (!conf) return null;
    return (
      <Tooltip title={`${t("contactProfile.confidence")}: ${Math.round(conf * 100)}%`}>
        <span
          className={classes.confidenceBadge}
          style={{ backgroundColor: getConfidenceColor(conf) + "30", color: getConfidenceColor(conf) }}
        >
          {Math.round(conf * 100)}%
        </span>
      </Tooltip>
    );
  };

  return (
    <Paper square variant="outlined" className={classes.profileContainer}>
      <Typography variant="subtitle1" style={{ marginBottom: 8 }}>
        🧠 {t("contactProfile.title")}
      </Typography>

      {/* Personal Info */}
      {personal && (
        <>
          <div className={classes.sectionTitle}>
            <LanguageIcon className={classes.sectionIcon} />
            {t("contactProfile.sections.personal")}
          </div>
          {personal.fullName && (
            <div className={classes.infoRow}>
              <span className={classes.infoLabel}>{t("contactProfile.labels.name")}:</span>
              <span className={classes.infoValue}>{personal.fullName}</span>
              <ConfidenceBadge fieldKey="fullName" />
            </div>
          )}
          {personal.preferredLanguage && (
            <div className={classes.infoRow}>
              <span className={classes.infoLabel}>{t("contactProfile.labels.language")}:</span>
              <span className={classes.infoValue}>
                {personal.preferredLanguage.toUpperCase()}
              </span>
            </div>
          )}
          {personal.country && (
            <div className={classes.infoRow}>
              <span className={classes.infoLabel}>{t("contactProfile.labels.country")}:</span>
              <span className={classes.infoValue}>{personal.country}</span>
            </div>
          )}
          <Divider className={classes.divider} />
        </>
      )}

      {/* Travel Info */}
      {travel && (travel.destinations?.length > 0 || travel.budgetRange || travel.duration) && (
        <>
          <div className={classes.sectionTitle}>
            <FlightIcon className={classes.sectionIcon} />
            {t("contactProfile.sections.travel")}
          </div>
          {travel.destinations?.length > 0 && (
            <div className={classes.infoRow}>
              <span className={classes.infoLabel}>{t("contactProfile.labels.destinations")}:</span>
              <div className={classes.chipContainer}>
                {travel.destinations.map((dest, i) => (
                  <Chip
                    key={i}
                    label={dest}
                    size="small"
                    className={classes.chip}
                    color="primary"
                    variant="outlined"
                  />
                ))}
              </div>
            </div>
          )}
          {travel.preferredMonths?.length > 0 && (
            <div className={classes.infoRow}>
              <span className={classes.infoLabel}>{t("contactProfile.labels.months")}:</span>
              <span className={classes.infoValue}>
                {travel.preferredMonths.join(", ")}
              </span>
            </div>
          )}
          {travel.duration && (
            <div className={classes.infoRow}>
              <span className={classes.infoLabel}>{t("contactProfile.labels.duration")}:</span>
              <span className={classes.infoValue}>{travel.duration}</span>
            </div>
          )}
          {travel.budgetRange && (
            <div className={classes.infoRow}>
              <span className={classes.infoLabel}>{t("contactProfile.labels.budget")}:</span>
              <span className={classes.infoValue}>
                {travel.budgetRange.currency || "$"}
                {travel.budgetRange.min?.toLocaleString()} - {travel.budgetRange.max?.toLocaleString()}
              </span>
              <ConfidenceBadge fieldKey="budgetRange" />
            </div>
          )}
          {travel.accommodationType && (
            <div className={classes.infoRow}>
              <span className={classes.infoLabel}>{t("contactProfile.labels.accommodation")}:</span>
              <span className={classes.infoValue}>{travel.accommodationType}</span>
            </div>
          )}
          <Divider className={classes.divider} />
        </>
      )}

      {/* Family Info */}
      {family && (family.adultsCount || family.childrenCount || family.specialRequirements?.length > 0) && (
        <>
          <div className={classes.sectionTitle}>
            <PeopleIcon className={classes.sectionIcon} />
            {t("contactProfile.sections.family")}
          </div>
          <Grid container spacing={1}>
            {family.adultsCount && (
              <Grid item xs={6}>
                <div className={classes.infoRow}>
                  <span className={classes.infoLabel}>{t("contactProfile.labels.adults")}:</span>
                  <span className={classes.infoValue}>{family.adultsCount}</span>
                </div>
              </Grid>
            )}
            {family.childrenCount !== undefined && family.childrenCount > 0 && (
              <Grid item xs={6}>
                <div className={classes.infoRow}>
                  <span className={classes.infoLabel}>{t("contactProfile.labels.children")}:</span>
                  <span className={classes.infoValue}>{family.childrenCount}</span>
                </div>
              </Grid>
            )}
          </Grid>
          {family.childrenAges?.length > 0 && (
            <div className={classes.infoRow}>
              <span className={classes.infoLabel}>{t("contactProfile.labels.ages")}:</span>
              <span className={classes.infoValue}>{family.childrenAges.join(", ")}</span>
            </div>
          )}
          {family.specialRequirements?.length > 0 && (
            <div style={{ marginTop: 4 }}>
              <span className={classes.infoLabel}>{t("contactProfile.labels.requirements")}:</span>
              <div className={classes.chipContainer}>
                {family.specialRequirements.map((req, i) => (
                  <Chip
                    key={i}
                    label={req}
                    size="small"
                    className={classes.chip}
                    icon={<RequirementsIcon style={{ fontSize: 12 }} />}
                  />
                ))}
              </div>
            </div>
          )}
          <Divider className={classes.divider} />
        </>
      )}

      {/* Interests */}
      {interests && (interests.primaryInterest || interests.travelStyle || interests.activityPreferences?.length > 0) && (
        <>
          <div className={classes.sectionTitle}>
            <HotelIcon className={classes.sectionIcon} />
            {t("contactProfile.sections.preferences")}
          </div>
          {interests.primaryInterest && (
            <div className={classes.infoRow}>
              <span className={classes.infoLabel}>{t("contactProfile.labels.interest")}:</span>
              <span className={classes.infoValue}>{interests.primaryInterest}</span>
            </div>
          )}
          {interests.travelStyle && (
            <div className={classes.infoRow}>
              <span className={classes.infoLabel}>{t("contactProfile.labels.style")}:</span>
              <Chip
                label={interests.travelStyle}
                size="small"
                className={classes.chip}
                color="secondary"
              />
            </div>
          )}
          {interests.activityPreferences?.length > 0 && (
            <div style={{ marginTop: 4 }}>
              <span className={classes.infoLabel}>{t("contactProfile.labels.activities")}:</span>
              <div className={classes.chipContainer}>
                {interests.activityPreferences.map((act, i) => (
                  <Chip
                    key={i}
                    label={act}
                    size="small"
                    className={classes.chip}
                    variant="outlined"
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Ready-to-Book Section */}
      <Paper className={classes.readyToBookSection} elevation={0}>
        <div 
          className={classes.readyToBookHeader}
          onClick={() => setExpanded(!expanded)}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <SummaryIcon style={{ fontSize: 18, color: "#25d366" }} />
            <Typography style={{ fontSize: 13, fontWeight: 600 }}>
              {t("contactProfile.readyToBook.title")}
            </Typography>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {readyStatus.status === "ready" && (
              <span className={classes.statusReady}>
                <ReadyIcon style={{ fontSize: 14 }} />
                {t("contactProfile.readyToBook.statusReady")}
              </span>
            )}
            {readyStatus.status === "pending" && (
              <span className={classes.statusPending}>
                <PendingIcon style={{ fontSize: 14 }} />
                {t("contactProfile.readyToBook.statusAlmost")}
              </span>
            )}
            {readyStatus.status === "missing" && (
              <span className={classes.statusMissing}>
                <MissingIcon style={{ fontSize: 14 }} />
                {t("contactProfile.readyToBook.statusMissing")}
              </span>
            )}
            {expanded ? <CollapseIcon /> : <ExpandIcon />}
          </div>
        </div>

        <Collapse in={expanded}>
          <Box mt={1}>
            {readyStatus.missingFields.length > 0 && (
              <Box mb={1}>
                <Typography style={{ fontSize: 11, color: "#666", marginBottom: 4 }}>
                  {t("contactProfile.readyToBook.missingInfo")}:
                </Typography>
                {readyStatus.missingFields.map((field, i) => (
                  <div key={i} className={classes.missingItem}>
                    • {field}
                  </div>
                ))}
              </Box>
            )}

            <Button
              variant="contained"
              color="primary"
              size="small"
              fullWidth
              className={classes.generateButton}
              onClick={handleGenerateSummary}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={14} /> : <SummaryIcon />}
            >
              {loading ? t("contactProfile.readyToBook.generating") : t("contactProfile.readyToBook.generateSummary")}
            </Button>

            {summary && (
              <Box className={classes.summaryBox}>
                <Button
                  size="small"
                  className={classes.copyButton}
                  onClick={handleCopySummary}
                  title={t("contactProfile.readyToBook.copyToClipboard")}
                >
                  <CopyIcon style={{ fontSize: 14 }} />
                </Button>
                <Typography style={{ fontSize: 11, fontWeight: 600, marginBottom: 8 }}>
                  {summary.status === "ready" ? `✅ ${t("contactProfile.readyToBook.statusReadyFull")}` : 
                   summary.status === "pending" ? `⏳ ${t("contactProfile.readyToBook.statusAlmostFull")}` : `❌ ${t("contactProfile.readyToBook.statusMoreInfo")}`}
                </Typography>
                {summary.summaryText}
              </Box>
            )}
          </Box>
        </Collapse>
      </Paper>

      {/* Metadata */}
      {profile.metadata?.lastUpdated && (
        <Typography
          style={{ fontSize: 10, color: "#999", marginTop: 8, textAlign: "right" }}
        >
          {t("contactProfile.updated")}: {new Date(profile.metadata.lastUpdated).toLocaleDateString()}
        </Typography>
      )}
    </Paper>
  );
};

export default ContactProfile;
